import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} AssignLicenseData
 * @property {number} sub_school_institution_id - Sub-school institution ID
 * @property {number} license_count - Number of licenses to assign
 * @property {string} [remark] - Remark for the license assignment
 */

/**
 * @description Custom hook to assign licenses to a school
 */
export const useAssignLicense = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { axiosInstance } = useAxios();

  /**
   * @description Assign licenses to a school
   * @async
   * @param {AssignLicenseData} licenseData - The license assignment data
   * @param {()=> void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<void>} Resolves once the API request completes
   */
  const assignLicense = async (licenseData, onSuccess) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);

      await axiosInstance.post(
        `${TENANT_DOMAIN}/organization/admin/licenses/assign`,
        {
          sub_school_institution_id: licenseData.sub_school_institution_id,
          license_count: licenseData.license_count,
          ...(licenseData.remark && { remark: licenseData.remark }),
        },
      );

      setSuccess(true);
      if (onSuccess) onSuccess();
      return true;
    } catch (err) {
      setSuccess(false);

      if (err?.response?.status === 500 || err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const apiErrors = err?.response?.data?.errors;
        const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

        setError(
          firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, success, assignLicense };
};
